import { useState, lazy, Suspense } from "react";
import { productPath } from "../../lib/productUrls";
import { Link } from "react-router-dom";
import { Heart, ChevronLeft, ChevronRight, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useWishlist } from "../../auth/WishlistContext";
import { useSettings } from "../../auth/SettingsContext";
import { useDepartments } from "../../lib/queries";
import { parseSizes } from "../../lib/sizes";
import { isOnSale, effectivePrice, discountPct } from "../../lib/price";
import ResponsiveImg from "./ResponsiveImg";
import VideoBadge from "./pdp/VideoBadge";

// iter291.6 — Lazy-loaded TryOnModal so the boutique-wide try-on
// surface can be triggered straight from the product thumbnail. The
// modal is only mounted when a customer actually opens it; the import
// happens at first click so the catalog grid stays light.
const TryOnModal = lazy(() => import("./TryOnModal"));

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const imgSrc = (url) => {
    if (!url) return "/default-item.svg";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
};

export default function ProductCard({ item }) {
    const { has: isWished, toggle: toggleWish } = useWishlist();
    const settings = useSettings();
    // WP3 — departments carry the per-dept try-on flag; cached 5 min so this
    // is effectively free once the grid has rendered once.
    const { data: departments = [] } = useDepartments();
    const [imgIdx, setImgIdx] = useState(0);
    const [tryOnOpen, setTryOnOpen] = useState(false);

    const wished = isWished(item.id);
    const outOfStock = !item.quantity || item.quantity <= 0;
    // iter291.6 — Try-on is a boutique-wide master switch (no per-item
    // toggle yet). Only surface the thumbnail pill when the admin has
    // turned it on AND the item is in stock (no point letting a
    // customer try on a sold-out piece). Default false so a slow
    // settings fetch never accidentally shows the pill.
    // WP3 — additionally gated on the item's department try-on flag so
    // non-garment depts (jewellery) never show a try-on affordance.
    // Default TRUE when the dept is missing → clothing behaviour unchanged.
    const dept = departments.find((d) => d.slug === item.department_slug);
    const deptTryonEnabled = dept ? dept.tryon_enabled !== false : true;
    const tryonEnabled = !!settings?.tryon_enabled && !outOfStock && deptTryonEnabled;

    const images = (item.photo_urls && item.photo_urls.length > 0)
        ? item.photo_urls
        : (item.photo_url ? [item.photo_url] : []);
    const hasMulti = images.length > 1;

    const toggleWl = (e) => {
        e.preventDefault(); e.stopPropagation();
        const wasWished = wished;
        toggleWish(item.id);
        toast.success(wasWished ? "Removed from wishlist" : "Added to wishlist");
    };

    const openTryOn = (e) => {
        // Open the modal in place — without this, the wrapping <Link>
        // would navigate to PDP and the customer would lose flow.
        e.preventDefault();
        e.stopPropagation();
        setTryOnOpen(true);
    };

    const prev = (e) => {
        e.preventDefault(); e.stopPropagation();
        setImgIdx((i) => (i - 1 + images.length) % images.length);
    };
    const next = (e) => {
        e.preventDefault(); e.stopPropagation();
        setImgIdx((i) => (i + 1) % images.length);
    };

    return (
        <>
            {/* h-full: in rails/grids the slot is stretched to the row's
                tallest card — fill it so bottoms align even when a card has
                no rating/discount row (uneven tiles caught by owner). */}
            <Link to={productPath(item)} className="product-card flex flex-col group h-full" data-testid={`shop-item-${item.id}`}>
            <div className="relative aspect-[4/5] overflow-hidden bg-stone-100">
                {images.length === 0 ? (
                    <img src="/default-item.svg" alt={item.name} className="absolute inset-0 w-full h-full object-cover object-top" />
                ) : (
                    <ResponsiveImg
                        src={images[imgIdx]}
                        alt={item.name}
                        sizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw"
                        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700"
                        onError={(e) => { e.target.src = "/default-item.svg"; }}
                    />
                )}

                {isOnSale(item) && !outOfStock && (
                    <span className="sr-only" data-testid={`sale-badge-${item.id}`}>{discountPct(item)}% OFF</span>
                )}

                {/* PDP-style video badge — appears on every product card that
                    has a `video_url`. Uses a scaled-down PDP badge so it reads
                    cleanly on small tiles too. Click opens the lightbox in
                    place (preventDefault on the inner button stops the
                    surrounding <Link> from navigating to PDP). */}
                {item.video_url && (
                    <VideoBadge
                        videoUrl={item.video_url}
                        posterUrl={images[0] || item.photo_url}
                        className="!bottom-2 !right-2 sm:!bottom-2.5 sm:!right-2.5 [&>span:nth-child(3)]:!w-11 [&>span:nth-child(3)]:!h-11 sm:[&>span:nth-child(3)]:!w-12 sm:[&>span:nth-child(3)]:!h-12 [&_svg]:!w-3 [&_svg]:!h-3"
                    />
                )}

                {outOfStock && (
                    <div className="absolute inset-0 bg-ivory/70 backdrop-blur-[1px] flex items-center justify-center pointer-events-none" data-testid={`oos-overlay-${item.id}`}>
                        <span className="px-3 py-1.5 bg-ink text-ivory text-[11px] font-semibold tracking-wider uppercase">Out of Stock</span>
                    </div>
                )}

                <button
                    onClick={toggleWl}
                    data-testid={`wishlist-${item.id}`}
                    className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-ink-soft hover:text-rosegold shadow-sm transition-colors"
                    aria-label="Wishlist"
                >
                    <Heart size={16} fill={wished ? "#C9A47A" : "none"} className={wished ? "text-rosegold" : ""} />
                </button>

                {/* iter291.18 — REMOVED the photo-overlay Try-on pill.
                    On small/medium tiles (especially in carousels on
                    home / category pages), top-left `absolute` covered
                    the model's face. Affordance moved into the text
                    block below — small, evident, and never over the
                    model. Card-photo area now stays uncluttered
                    (wishlist heart + video badge only). */}

                {hasMulti && (
                    <>
                        <button
                            onClick={prev}
                            data-testid={`card-prev-${item.id}`}
                            aria-label="Previous image"
                            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-stone-700 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={next}
                            data-testid={`card-next-${item.id}`}
                            aria-label="Next image"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-stone-700 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <ChevronRight size={16} />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 pointer-events-none">
                            {images.map((_, i) => (
                                <span key={i} className={`h-1 rounded-full transition-all ${i === imgIdx ? "bg-white w-4" : "bg-white/60 w-1"}`} />
                            ))}
                        </div>
                    </>
                )}

                {/* Jul 2026 (Sagar) — try-on moved OFF the image (the small
                    glassy pill blended into warm fabric shots) into a
                    prominent full-width button in the info block below. */}
            </div>

            <div className="px-3.5 py-3.5 flex-1 flex flex-col">
                <p className="overline text-rosegold mb-1" data-testid={`card-category-${item.id}`}>{item.category}</p>
                <h3 className="text-[13px] font-medium text-ink leading-snug line-clamp-2 min-h-[2.4rem]">{item.name}</h3>

                {/* Subtle rating row — only renders when at least one verified
                    review exists, per the brief ("show ratings only if real
                    reviews exist; no oversized stars; muted boutique style"). */}
                {(item.rating_count || 0) > 0 && (
                    <p
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-soft"
                        data-testid={`rating-${item.id}`}
                    >
                        <Star size={11} className="text-rosegold fill-rosegold" />
                        <span className="font-num tabular-nums font-semibold text-ink">
                            {Number(item.rating_avg).toFixed(1)}
                        </span>
                        <span className="text-stone-400">({item.rating_count})</span>
                    </p>
                )}

                {(() => {
                    const sizes = parseSizes(item.sizes);
                    return (
                        <p className="text-[11px] text-ink-soft mt-1 min-h-[1rem] truncate tracking-wide">
                            {sizes.length > 0 ? sizes.join(" · ") : "\u00A0"}
                        </p>
                    );
                })()}
                <div className="mt-auto pt-2.5 flex items-baseline gap-2 flex-wrap min-w-0">
                    <p className="font-num text-[19px] font-bold text-ink leading-none tracking-tight" data-testid={`price-${item.id}`}>{fmtINR(effectivePrice(item))}</p>
                    {isOnSale(item) && (
                        <>
                            <p className="font-num text-[12px] text-ink-soft/60 line-through font-semibold" data-testid={`original-price-${item.id}`}>{fmtINR(item.selling_price)}</p>
                            {/* Jul 2026 (S1) — a wall of "5% OFF" badges cheapens a
                                premium boutique more than it converts. Under 10% we
                                show absolute savings ("Save ₹150") — concrete and
                                un-cheapening; 10%+ keeps the punchy "% OFF". */}
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-wine/10 text-wine text-[10px] font-bold tracking-wide leading-none" data-testid={`discount-chip-${item.id}`}>
                                {discountPct(item) >= 10
                                    ? `${discountPct(item)}% OFF`
                                    : `Save ${fmtINR(item.selling_price - effectivePrice(item))}`}
                            </span>
                        </>
                    )}
                </div>

                {/* Prominent full-width Try-on — evident, never over the model.
                    Only when the boutique has try-on on AND the piece is in stock. */}
                {tryonEnabled && (
                    <button
                        onClick={openTryOn}
                        data-testid={`tryon-card-${item.id}`}
                        className="mt-3 w-full h-9 rounded-full inline-flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-wine bg-rosegold/15 ring-1 ring-rosegold/45 hover:bg-rosegold/25 hover:ring-rosegold active:scale-[0.98] transition-all"
                        aria-label={`Try on ${item.name}`}
                    >
                        <Sparkles size={12} strokeWidth={2.4} /> Try it on
                    </button>
                )}
            </div>
        </Link>
        {/* iter291.6 — Lazy-mounted TryOnModal opened from the
            thumbnail pill. Rendered as a SIBLING of the Link (not a
            descendant) so clicks inside the modal don't bubble up to
            the Link and trigger an unwanted PDP navigation. The
            Suspense fallback is null — the chunk is small and a
            flash would feel jarring. */}
        {tryOnOpen && (
            <Suspense fallback={null}>
                <TryOnModal
                    itemId={item.id}
                    itemName={item.name}
                    itemPhoto={imgSrc(images[0] || item.photo_url)}
                    itemCategory={item.category}
                    onClose={() => setTryOnOpen(false)}
                />
            </Suspense>
        )}
        </>
    );
}
